import {
  CasNotificationsQuery,
  CasNotificationsQueryResult,
  useCasNotificationsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  UseRecipientsFn,
  useRecipientsFn,
} from 'components/CIMSNotifications/hooks/useRecipientsFn'
import {
  CASNotificationEvent,
  CASNotificationEventItem,
} from '@cims-online/shared'
import { useProjectContext } from 'context/ProjectContext'

type Query = CasNotificationsQuery
type CASNotification = Query['casNotifications']['records'][number]
type CASRoutingGroup = Query['casRoutingGroups']['records'][number]
type CASNotificationsContext = Pick<
  CasNotificationsQueryResult,
  'loading' | 'refetch'
> & {
  casNotifications: CASNotification[]
  casRoutingGroups: CASRoutingGroup[]
  recipients: UseRecipientsFn<CASNotification['recipients']>
  approverEvents: Readonly<CASNotificationEventItem>[]
}

const { Provider, useContextFn } = createStrictContext<CASNotificationsContext>(
  'CASNotificationsContext'
)

export const useCASNotificationsContext = useContextFn

const CASNotificationsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const { data, loading, refetch } = useCasNotificationsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })

  const recipients = useRecipientsFn({
    data,
    getter: (id, { casNotifications }) =>
      casNotifications.records.find(
        ({ notificationEventId }) => id === notificationEventId
      )?.recipients,
    fallback: {
      __typename: 'CASNotificationRecipients',
      every: null,
      byRoutingGroup: [],
    },
  })
  return (
    <Provider
      value={{
        loading,
        refetch,
        recipients,
        casRoutingGroups: data?.casRoutingGroups.records ?? [],
        casNotifications: data?.casNotifications.records ?? [],
        approverEvents: [
          CASNotificationEvent.Edited,
          CASNotificationEvent.RoutedToGroupReview,
        ],
      }}
    >
      {children}
    </Provider>
  )
}

export default CASNotificationsProvider
